import type { Form, FormField } from '../../interfaces/form.interface';
import { ReactiveFormValueUtils } from '../../model/reactive-form-value.utils';

export type ReactiveDependencyKind =
    | 'dropdown'
    | 'filter'
    | 'visibility'
    | 'computed'
    | 'dynamicType'
    | 'appendData'
    | 'legacy';

export interface ReactiveFieldDependency {
    readonly source: string;
    readonly target: string;
    readonly kind: ReactiveDependencyKind;
    readonly clearOnChange?: boolean;
    readonly reloadOnChange?: boolean;
    readonly filterLocally?: boolean;
    readonly preserveOnHydration?: boolean;
    readonly legacy?: boolean;
}

export type ReactiveDependencyCycle = readonly string[];

/**
 * Centralizes dependency discovery for reactive-form fields.
 *
 * New forms can provide explicit dependencies through `form.dependencies` or
 * `field.dependencies`. Legacy forms are adapted from `vinculatedFields`,
 * `dataSource.dependsOn` and dynamic type dependencies, so old definitions keep
 * working while the runtime stops reading those mechanisms directly.
 */
export class ReactiveDependencyGraph<T> {
    private readonly dependencies: readonly ReactiveFieldDependency[];
    private readonly dependenciesByTarget = new Map<string, ReactiveFieldDependency[]>();
    private readonly dependenciesBySource = new Map<string, ReactiveFieldDependency[]>();

    constructor(form: Form<T> | null) {
        this.dependencies = this.buildDependencies(form);
        this.indexDependencies(this.dependencies);
    }

    public getDependencies(): readonly ReactiveFieldDependency[] {
        return this.dependencies;
    }

    public getDependenciesForTarget(
        target: string | undefined,
        kind?: ReactiveDependencyKind | readonly ReactiveDependencyKind[],
    ): readonly ReactiveFieldDependency[] {
        if (!target) {
            return [];
        }

        return this.filterByKind(this.dependenciesByTarget.get(target) ?? [], kind);
    }

    public getDependenciesFromSource(
        source: string | undefined,
        kind?: ReactiveDependencyKind | readonly ReactiveDependencyKind[],
    ): readonly ReactiveFieldDependency[] {
        if (!source) {
            return [];
        }

        return this.filterByKind(this.dependenciesBySource.get(source) ?? [], kind);
    }

    public getSourcesForTarget(
        target: string | undefined,
        kind?: ReactiveDependencyKind | readonly ReactiveDependencyKind[],
    ): readonly string[] {
        return this.getDependenciesForTarget(target, kind).map((dependency) => dependency.source);
    }

    public getTargetsForSource(
        source: string | undefined,
        kind?: ReactiveDependencyKind | readonly ReactiveDependencyKind[],
    ): readonly string[] {
        return this.getDependenciesFromSource(source, kind).map((dependency) => dependency.target);
    }

    public hasChangedDependency(
        target: string | undefined,
        changedFields: readonly string[],
        kind?: ReactiveDependencyKind | readonly ReactiveDependencyKind[],
    ): boolean {
        if (!target || changedFields.length === 0) {
            return false;
        }

        const changedFieldSet = new Set(changedFields);

        return this.getDependenciesForTarget(target, kind)
            .some((dependency) => changedFieldSet.has(dependency.source));
    }

    public getChangedDependency(
        target: string | undefined,
        changedFields: readonly string[],
        kind?: ReactiveDependencyKind | readonly ReactiveDependencyKind[],
    ): string | null {
        if (!target || changedFields.length === 0) {
            return null;
        }

        const changedFieldSet = new Set(changedFields);
        const dependency = this.getDependenciesForTarget(target, kind)
            .find((item) => changedFieldSet.has(item.source));

        return dependency?.source ?? changedFields[0] ?? null;
    }

    public hasDependencies(target: string | undefined, kind?: ReactiveDependencyKind | readonly ReactiveDependencyKind[]): boolean {
        return this.getDependenciesForTarget(target, kind).length > 0;
    }

    public dependenciesHaveValue(
        target: string | undefined,
        getValue: (fieldName: string) => unknown,
        kind?: ReactiveDependencyKind | readonly ReactiveDependencyKind[],
    ): boolean {
        const dependencies = this.getDependenciesForTarget(target, kind);

        if (dependencies.length === 0) {
            return true;
        }

        return dependencies.every((dependency) => ReactiveFormValueUtils.hasNonEmptyValue(getValue(dependency.source)));
    }

    public getHydratedSources(
        getValue: (fieldName: string) => unknown,
        kind?: ReactiveDependencyKind | readonly ReactiveDependencyKind[],
    ): string[] {
        const sources = new Set<string>();

        for (const dependency of this.filterByKind(this.dependencies, kind)) {
            if (ReactiveFormValueUtils.hasNonEmptyValue(getValue(dependency.source))) {
                sources.add(dependency.source);
            }
        }

        return Array.from(sources);
    }

    public getCycles(): ReactiveDependencyCycle[] {
        const adjacency = new Map<string, Set<string>>();

        for (const dependency of this.dependencies) {
            const targets = adjacency.get(dependency.source) ?? new Set<string>();

            targets.add(dependency.target);
            adjacency.set(dependency.source, targets);
        }

        const cycles: ReactiveDependencyCycle[] = [];
        const visited = new Set<string>();
        const visiting = new Set<string>();

        const visit = (node: string, path: readonly string[]): void => {
            if (visiting.has(node)) {
                const cycleStart = path.indexOf(node);

                if (cycleStart >= 0) {
                    cycles.push([...path.slice(cycleStart)]);
                }

                return;
            }

            if (visited.has(node)) {
                return;
            }

            visiting.add(node);

            for (const nextNode of adjacency.get(node) ?? []) {
                visit(nextNode, [...path, nextNode]);
            }

            visiting.delete(node);
            visited.add(node);
        };

        for (const node of adjacency.keys()) {
            visit(node, [node]);
        }

        return this.deduplicateCycles(cycles);
    }

    private buildDependencies(form: Form<T> | null): readonly ReactiveFieldDependency[] {
        const dependencies: ReactiveFieldDependency[] = [];

        for (const dependency of form?.dependencies ?? []) {
            this.addDependency(dependencies, dependency);
        }

        for (const field of this.getFields(form)) {
            for (const dependency of field.dependencies ?? []) {
                this.addDependency(dependencies, dependency);
            }

            this.addDropdownDependencies(dependencies, field);
            this.addComputedDependencies(dependencies, field);
            this.addDynamicTypeDependencies(dependencies, field);
            this.addLegacyVinculatedDependencies(dependencies, field);
        }

        return dependencies;
    }

    private addDropdownDependencies(dependencies: ReactiveFieldDependency[], field: FormField<T>): void {
        for (const source of field.dataSource?.dependsOn ?? []) {
            this.addDependency(dependencies, {
                source,
                target: field.name,
                kind: 'dropdown',
                clearOnChange: field.dependencyBehaviour?.clearOnParentChange ?? true,
                reloadOnChange: field.dependencyBehaviour?.reloadOnParentChange ?? false,
                filterLocally: field.dependencyBehaviour?.filterLocally ?? false,
                preserveOnHydration: field.dependencyBehaviour?.clearOnHydration === false,
                legacy: true,
            });
        }
    }

    private addComputedDependencies(dependencies: ReactiveFieldDependency[], field: FormField<T>): void {
        for (const source of this.toDependencyArray(field.computed?.dependsOn)) {
            this.addDependency(dependencies, {
                source,
                target: field.name,
                kind: 'computed',
            });
        }
    }

    private addDynamicTypeDependencies(dependencies: ReactiveFieldDependency[], field: FormField<T>): void {
        for (const source of this.toDependencyArray(field.input?.dynamicTypeConfiguration?.dependsOn)) {
            this.addDependency(dependencies, {
                source,
                target: field.name,
                kind: 'dynamicType',
                legacy: true,
            });
        }
    }

    private addLegacyVinculatedDependencies(dependencies: ReactiveFieldDependency[], field: FormField<T>): void {
        for (const source of field.vinculatedFields ?? []) {
            const hasFilter = Boolean(field.input?.dropdownConfiguration?.filter);
            const hasAppendData = typeof field.input?.dropdownConfiguration?.appendData === 'function';
            const hasVisibility = Boolean(field.turnVisible);
            const hasComputed = Boolean(field.updateValue || field.computed);

            this.addDependency(dependencies, {
                source,
                target: field.name,
                kind: 'legacy',
                legacy: true,
            });

            if (hasFilter) {
                this.addDependency(dependencies, {
                    source,
                    target: field.name,
                    kind: 'filter',
                    clearOnChange: field.dependencyBehaviour?.clearOnParentChange ?? true,
                    preserveOnHydration: field.dependencyBehaviour?.clearOnHydration === false,
                    legacy: true,
                });
            }

            if (hasAppendData) {
                this.addDependency(dependencies, {
                    source,
                    target: field.name,
                    kind: 'appendData',
                    legacy: true,
                });
            }

            if (hasVisibility) {
                this.addDependency(dependencies, {
                    source,
                    target: field.name,
                    kind: 'visibility',
                    legacy: true,
                });
            }

            if (hasComputed) {
                this.addDependency(dependencies, {
                    source,
                    target: field.name,
                    kind: 'computed',
                    legacy: true,
                });
            }
        }
    }

    private addDependency(dependencies: ReactiveFieldDependency[], dependency: ReactiveFieldDependency): void {
        if (!dependency.source || !dependency.target) {
            return;
        }

        const exists = dependencies.some((item) =>
            item.source === dependency.source &&
            item.target === dependency.target &&
            item.kind === dependency.kind,
        );

        if (!exists) {
            dependencies.push(dependency);
        }
    }

    private indexDependencies(dependencies: readonly ReactiveFieldDependency[]): void {
        for (const dependency of dependencies) {
            this.addToIndex(this.dependenciesByTarget, dependency.target, dependency);
            this.addToIndex(this.dependenciesBySource, dependency.source, dependency);
        }
    }

    private addToIndex(
        index: Map<string, ReactiveFieldDependency[]>,
        key: string,
        dependency: ReactiveFieldDependency,
    ): void {
        const currentDependencies = index.get(key) ?? [];

        currentDependencies.push(dependency);
        index.set(key, currentDependencies);
    }

    private getFields(form: Form<T> | null): readonly FormField<T>[] {
        return (form?.sections ?? []).flatMap((section) => section.fields ?? []);
    }

    private toDependencyArray(dependencies: string | readonly string[] | undefined): readonly string[] {
        if (!dependencies) {
            return [];
        }

        return typeof dependencies === 'string' ? [dependencies] : dependencies;
    }

    private filterByKind(
        dependencies: readonly ReactiveFieldDependency[],
        kind: ReactiveDependencyKind | readonly ReactiveDependencyKind[] | undefined,
    ): readonly ReactiveFieldDependency[] {
        if (!kind) {
            return dependencies;
        }

        const kinds = Array.isArray(kind) ? kind : [kind];

        return dependencies.filter((dependency) => kinds.includes(dependency.kind));
    }

    private deduplicateCycles(cycles: readonly ReactiveDependencyCycle[]): ReactiveDependencyCycle[] {
        const seen = new Set<string>();
        const result: ReactiveDependencyCycle[] = [];

        for (const cycle of cycles) {
            const key = cycle.join('>');

            if (seen.has(key)) {
                continue;
            }

            seen.add(key);
            result.push(cycle);
        }

        return result;
    }
}
